import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Title from '../../components/common/Title';
import Card from '../../components/elements/Card';
import Icon from '../../components/common/Icon';
import Loading from '../../components/common/Loading';
import { apiRequest, shortDate } from '../../utils';

/* What the company actually bought, per edition and per ticket type — the contact
   person's counterpart to the Bravos company-ticket screen, for every business model.

   A team package hands out its seats onto the team, so the headline number is
   "claimed of total": how many of the seats paid for actually have someone in them.
   Anything still open is a colleague who has not been added yet, which is why each
   edition links straight through to the team screens. Read-only on purpose — seats are
   handed out where the teams are managed, this is the tally. */

const Tickets = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    apiRequest('GET', '/shop/tickets')
      .then(setData)
      .catch((err) => {
        if ((err.message || '') === 'notAuthorized') { navigate('/home'); return; }
        setFailed(true);
      });
  }, [navigate]);

  const events = data?.events || [];
  const pool = data?.pool || [];

  return (
    <Base activeNav="shop">
      <Title>
        <Icon name="ticket" theme="primary" className="me-2" style={{ fontSize: '1.4rem' }} />
        {t('yourTickets')}
      </Title>

      {!data && !failed && <Loading size="lg" />}

      {failed && (
        <Card className="text-center">
          <Icon name="info" theme="primary" style={{ fontSize: '1.6rem' }} />
          <p className="mt-2 mb-0">{t('somethingWentWrong')}</p>
        </Card>
      )}

      {data && events.length === 0 && pool.length === 0 && (
        <Card className="text-center">
          <Icon name="ticket" theme="muted" style={{ fontSize: '1.6rem' }} />
          <p className="mt-2 mb-0">{t('noCompanyTickets')}</p>
        </Card>
      )}

      {data && events.length > 0 && (
        <Card>
          <div className="d-flex justify-content-between align-items-baseline">
            <span style={{ fontWeight: 600 }}>{t('seatsOverview')}</span>
            <span className="text-muted" style={{ fontSize: '0.9rem' }}>
              {t('seatsClaimedOfTotal', {
                claimed: (data.seats_total || 0) - (data.seats_open || 0),
                total: data.seats_total || 0,
              })}
            </span>
          </div>
        </Card>
      )}

      {events.map((ev) => (
        <Card key={ev.event_id || 'loose'}>
          <div className="d-flex justify-content-between align-items-baseline mb-2">
            <span style={{ fontWeight: 600, minWidth: 0 }}>
              {ev.event_name || t('walletUnassigned')}
            </span>
            <span className="text-muted flex-shrink-0" style={{ fontSize: '0.85rem' }}>
              {shortDate(ev.event_date, i18n.language)}
              {ev.is_past ? ` · ${t('past')}` : ''}
            </span>
          </div>

          {ev.types.map((ty, i) => (
            <div key={i} className="d-flex justify-content-between" style={{ fontSize: '0.92rem' }}>
              <span className="text-muted" style={{ minWidth: 0 }}>
                {ty.emoji ? `${ty.emoji} ` : ''}{ty.type_name || t('ticket')}
              </span>
              <span className="ms-2 flex-shrink-0">
                {ty.claimed}/{ty.total}
                {ty.unclaimed > 0 && (
                  <span style={{ color: 'var(--bs-danger, #d9534f)' }}>
                    {' · '}{t('openSeatsCount', { n: ty.unclaimed })}
                  </span>
                )}
              </span>
            </div>
          ))}

          {/* Open seats are the actionable part: somebody still has to be put in them,
              and that happens on the team screens. */}
          {ev.seats_open > 0 && !ev.is_past && (
            <button type="button" className="btn-light p-2 mt-2"
                    style={{ borderRadius: '6px', fontSize: '0.85rem', border: 'none' }}
                    onClick={() => navigate('/company-readiness')}>
              <Icon name="user-add" className="me-1" />{t('fillOpenSeats')}
            </button>
          )}
        </Card>
      ))}

      {/* Seats the company owns that are not on an edition yet (a membership pool, or a
          seat returned by a cancelled booking). Empty for classic team packages. */}
      {pool.length > 0 && (
        <>
          <Title heading="5" my="3">{t('availableTickets')}</Title>
          {pool.map((ty) => (
            <Card key={ty.code} className="d-flex justify-content-between">
              <span>{ty.emoji ? `${ty.emoji} ` : ''}{ty.name}</span>
              <span className="text-muted">{t('poolOpenOfTotal', { open: ty.open, total: ty.total })}</span>
            </Card>
          ))}
        </>
      )}
    </Base>
  );
};

export default Tickets;
